// src/pages/channels/ChannelsList.js
import { useState, useEffect } from 'react';
import { 
  MessageSquare, Plus, Settings, Trash2, 
  CheckCircle, XCircle, Clock,
  Zap
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { channelService, agentService } from '../../services/PlatformService';

const ChannelsList = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Carregar channels e agentes
      const [channelsRes, agentsRes] = await Promise.all([
        channelService.listChannels(),
        agentService.getUserAgents()
      ]);
      
      setChannels(channelsRes.data || []);
      setAgents(agentsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChannelIcon = (type) => {
    switch (type) {
      case 'whatsapp_business':
        return (
          <img 
            src={"https://img.icons8.com/?size=512&id=16713&format=png"} 
            alt="WhatsApp" 
            className="w-5 h-5"
          />
        );
      case 'instagram_direct':
        return (
          <img 
            src={"https://upload.wikimedia.org/wikipedia/commons/thumb/e/e7/Instagram_logo_2016.svg/2000px-Instagram_logo_2016.svg.png"} 
            alt="instagram_direct" 
            className="w-5 h-5"
          />
        );
      case 'messenger':
        return (
          <img 
            src={"https://upload.wikimedia.org/wikipedia/commons/b/be/Facebook_Messenger_logo_2020.svg"} 
            alt="messenger" 
            className="w-5 h-5"
          />
        );
      case 'integration':
        return (
          <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
        );
      case 'platform':
        return <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-white" />
          </div>
      default:
        return <MessageSquare className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'active': { icon: <CheckCircle className="w-4 h-4" />, color: 'bg-green-100 text-green-700', label: 'Ativo' },
      'pending': { icon: <Clock className="w-4 h-4" />, color: 'bg-yellow-100 text-yellow-700', label: 'Pendente' },
      'inactive': { icon: <XCircle className="w-4 h-4" />, color: 'bg-red-100 text-red-700', label: 'Inativo' },
      'error': { icon: <XCircle className="w-4 h-4" />, color: 'bg-red-100 text-red-700', label: 'Erro' }
    };
    
    const cfg = config[status] || config.inactive;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${cfg.color}`}>
        {cfg.icon}
        {cfg.label}
      </span>
    );
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm('Tem certeza que deseja excluir este canal?')) return;
    
    try {
      await channelService.deleteChannel(channelId);
      await loadData();
    } catch (error) {
      console.error('Error deleting channel:', error);
      alert('Erro ao excluir canal');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando canais...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-800 to-green-600 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
                <MessageSquare className="w-10 h-10" />
                Canais de Atendimento
              </h1>
              <p className="text-xl opacity-90">
                Conecte seus funcionários aos canais de comunicação
              </p>
            </div>
            <button
              onClick={() => navigate('/channels/new')}
              className="flex items-center gap-2 px-6 py-3 bg-white text-green-600 rounded-2xl font-semibold hover:bg-gray-100 transition-all transform hover:scale-[1.02]"
            >
              <Plus className="w-5 h-5" />
              Novo Canal
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {channels.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <MessageSquare className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Nenhum canal configurado ainda
            </h2>
            <p className="text-gray-600 mb-6">
              Configure canais de comunicação para seus funcionários atenderem os clientes
            </p>
            <button
              onClick={() => navigate('/channels/new')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700"
            >
              <Plus className="w-5 h-5" />
              Configurar Primeiro Canal
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {channels.map(channel => {
              const agent = agents.find(a => a.id === channel.agent_id);
              
              return (
                <div key={channel.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {getChannelIcon(channel.type)}
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {channel.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {agent?.name || 'Agente não encontrado'}
                        </p>
                      </div>
                    </div>
                    {getStatusBadge(channel.status)}
                  </div>

                  {/* Stats */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Mensagens</span>
                      <span className="font-semibold">{channel.stats?.total_messages || 0}</span>
                    </div>
                    {channel.stats?.last_message_at && (
                      <p className="text-xs text-gray-500 mt-1">
                        Última: {new Date(channel.stats.last_message_at).toLocaleString('pt-BR')}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/channels/${channel.id}`}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all"
                    >
                      <Settings className="w-4 h-4" />
                      Configurar
                    </Link>
                    <button
                      onClick={() => handleDeleteChannel(channel.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelsList;